import React, { useState, FC, MouseEvent } from "react";

import { QueryObserverResult } from "@tanstack/react-query";
import { toast } from "react-toastify";

import AiIcon from "~/assets/images/ai_icon/kagamin_big_smile.png";

import {
  useApisMembersEmployeeSurveySubmissionsEmployeeSurveySubmissionRepliesCreate,
  useEditorState,
  useEditorStateLinkContentGetter,
  useApisMembersEmployeeSurveySubmissionsAiResponsesShow,
  useProvidersCurrentEmployee,
  useClipboard,
  useApisMembersCompanyFeatureFlagsIndex,
} from "~/hooks";

import {
  EmployeeSurveySubmissionReplyDetailWithEditForm,
  EmployeeSurveySubmissionDetail,
  TextEditor,
  PostHeader,
} from "~/components/organisms";

import {
  EmployeeSurveySubmissionType,
  EmployeeSurveySubmissionReplyType,
  MentionSuggestEmployeeType,
  PreviewWithFileType,
  ApiMemberEmployeeSurveySubmissionRepliesIndexResponseType,
  PostDetailMenuItemType,
} from "~/domains";

import { Href } from "~/constants/postUrl";

import { DotLoadingAnimation, PostDetailWrapper } from "~/ui";

type PropsType = {
  highlightEmployeeSurveySubmissionReplyId?: string;
  employeeSurveySubmission: EmployeeSurveySubmissionType;
  employeeSurveySubmissionReplies: EmployeeSurveySubmissionReplyType[];
  mentions: MentionSuggestEmployeeType[];
  className?: string;
  refetch: () => Promise<
    QueryObserverResult<
      ApiMemberEmployeeSurveySubmissionRepliesIndexResponseType,
      unknown
    >
  >;
};

export const EmployeeSurveySubmissionRepliesWithEmployeeSurveySubmission: FC<
  PropsType
> = ({
  highlightEmployeeSurveySubmissionReplyId = "",
  employeeSurveySubmission,
  employeeSurveySubmissionReplies,
  mentions,
  className = "",
  refetch,
}: PropsType) => {
  const { clipboardToCopy } = useClipboard();
  const { currentEmployee } = useProvidersCurrentEmployee();
  const [files, setFiles] = useState<PreviewWithFileType[]>([]);
  const [
    {
      value: editorState,
      onChange: setEditorState,
      jsonContent,
      hasText,
      setPlainText,
    },
  ] = useEditorState();

  // TextAreaモード用のplain text管理
  const [plainText, setPlainTextValue] = useState("");
  const { data: featureFlagsData } = useApisMembersCompanyFeatureFlagsIndex();
  const useTextAreaForm =
    featureFlagsData?.features.find((f) => f.key === "use_textarea_form")
      ?.enabled || false;

  const { fetchedData } = useEditorStateLinkContentGetter({
    watchEditorState: editorState,
    isWatch: true,
  });

  const { mutate, isLoading } =
    useApisMembersEmployeeSurveySubmissionsEmployeeSurveySubmissionRepliesCreate();

  const shouldAiFeedback =
    employeeSurveySubmission.employeeSurvey.shouldAiFeedback;
  const isMyEmployeeSurveySubmission =
    employeeSurveySubmission.employee.id === currentEmployee?.id;

  const { data: employeeSurveySubmissionsAiResponseData, isFetching } =
    useApisMembersEmployeeSurveySubmissionsAiResponsesShow({
      employeeSurveySubmissionId: employeeSurveySubmission.id,
    });

  const convertToDraft = (content: string) => {
    return {
      blocks: [
        {
          key: "cdmn6",
          text: content,
          type: "unstyled",
          depth: 0,
          entityRanges: [],
          inlineStyleRanges: [],
        },
      ],
      entityMap: {},
    };
  };

  const handleSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // TextAreaモードの場合はplainTextをチェック、通常モードはhasTextをチェック
    const hasContent = useTextAreaForm ? plainText.trim() !== "" : hasText;
    if (!hasContent) {
      toast.error("テキストが空です");
      return;
    }

    // TextAreaモードの場合はplainTextを使用、通常モードはeditorStateを使用
    const body = {
      content: useTextAreaForm ? convertToDraft(plainText) : jsonContent,
      contentText: useTextAreaForm ? plainText : undefined,
      attachFiles: files,
      quotePostId: fetchedData?.id,
    };

    mutate(
      {
        employeeSurveySubmissionId: employeeSurveySubmission.id,
        body,
      },
      {
        onSuccess: async () => {
          setPlainText("");
          setPlainTextValue(""); // TextAreaモード用のplain textもクリア
          setFiles([]);
          await refetch();
        },
      },
    );
  };

  const detailItems: PostDetailMenuItemType[] = [
    {
      icon: "ioCopyOutline",
      text: "リンクをコピー",
      onClick: () =>
        clipboardToCopy(
          `${location.origin}${Href.EmployeeSurveySubmission(employeeSurveySubmission.id)}`,
        ),
      disabled: false,
    },
  ];

  return (
    <div className={className}>
      <div className="space-y-6">
        <EmployeeSurveySubmissionDetail
          employeeSurveySubmission={employeeSurveySubmission}
          openReply={() => refetch()}
          canDelete={false}
        />
        {!isFetching &&
          employeeSurveySubmissionsAiResponseData?.employeeSurveySubmissionAiResponse && (
            <PostDetailWrapper isHighlight={false}>
              <PostHeader
                miniText={
                  employeeSurveySubmissionsAiResponseData
                    ?.employeeSurveySubmissionAiResponse.createdAt
                }
                postedEmployee={{
                  id: "",
                  name: "かがみんAI",
                  avatarUrl: AiIcon as string,
                }}
                menuItems={detailItems}
              />
              <div className="mt-2 whitespace-pre-wrap pl-16">
                {
                  employeeSurveySubmissionsAiResponseData
                    .employeeSurveySubmissionAiResponse?.answer
                }
              </div>
            </PostDetailWrapper>
          )}
        {shouldAiFeedback &&
          isMyEmployeeSurveySubmission &&
          !isFetching &&
          !employeeSurveySubmissionsAiResponseData?.employeeSurveySubmissionAiResponse && (
            <DotLoadingAnimation className="justify-center" />
          )}
        <div>
          {employeeSurveySubmissionReplies.map((reply) => (
            <EmployeeSurveySubmissionReplyDetailWithEditForm
              key={reply.id}
              employeeSurveySubmissionReply={reply}
              mentions={mentions}
              refetch={refetch}
              isHighlight={
                reply.id === highlightEmployeeSurveySubmissionReplyId
              }
            />
          ))}
        </div>
        <TextEditor
          submitButton={{
            isSubmitting: isLoading,
            onSubmit: handleSubmit,
          }}
          mentions={mentions}
          editorState={editorState}
          setEditorState={setEditorState}
          files={files}
          onChangeFiles={setFiles}
          blockQuote={fetchedData}
          isEmojiPickerTop
          plainTextValue={plainText}
          onPlainTextChange={setPlainTextValue}
        />
      </div>
    </div>
  );
};
